import React, { useEffect, useState } from 'react';
import { Save, Plus, Phone, Mail, MapPin, Globe, Facebook, Instagram, Youtube, Twitter, Building2, FileText, Clock } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const GROUPS = [
  {
    label: 'İletişim Bilgileri', icon: Phone,
    desc: 'İletişim ve footer\'da gösterilen bilgiler',
    fields: [
      { key: 'site_phone', label: 'Telefon', icon: Phone, placeholder: '0212 274 16 84' },
      { key: 'site_email', label: 'E-posta', icon: Mail, placeholder: 'info@yekuv.org' },
      { key: 'site_address', label: 'Adres', icon: MapPin, placeholder: 'Esentepe Mah. ...', long: true },
      { key: 'site_working_hours', label: 'Çalışma Saatleri', icon: Clock, placeholder: 'Pzt – Cum: 09:00 – 18:00' },
      { key: 'kvkk_email', label: 'KVKK İletişim E-postası', icon: Mail, placeholder: 'kvkk@yekuv.org' },
    ],
  },
  {
    label: 'Sosyal Medya', icon: Globe,
    desc: 'Footer\'da gösterilecek sosyal medya bağlantıları',
    fields: [
      { key: 'social_facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/...' },
      { key: 'social_twitter', label: 'Twitter / X', icon: Twitter, placeholder: 'https://x.com/...' },
      { key: 'social_instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/...' },
      { key: 'social_youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/...' },
    ],
  },
  {
    label: 'Footer & Hakkımızda', icon: FileText,
    desc: 'Footer\'daki kısa açıklama metni',
    fields: [
      { key: 'footer_about', label: 'Footer Açıklama', icon: FileText, placeholder: 'YEKÜV — eğitim için...', long: true },
    ],
  },
  {
    label: 'Form Bildirimleri', icon: Mail,
    desc: 'Form başvuruları geldiğinde bildirim gönderilecek e-posta',
    fields: [
      { key: 'volunteer_notify_email', label: 'Gönüllü Formu Bildirim E-postası', icon: Mail, placeholder: 'gonullu@yekuv.org' },
    ],
  },
  {
    label: 'Üst Bar / Panel Butonu', icon: Globe,
    desc: 'Sağ üstteki PANEL butonunun bağlantısı ve metni',
    fields: [
      { key: 'panel_button_url', label: 'Panel Butonu Adresi', icon: Globe, placeholder: '/admin/login veya https://...' },
      { key: 'panel_button_label', label: 'Panel Butonu Metni', icon: FileText, placeholder: 'Panel' },
    ],
  },
  {
    label: 'Uluslararası Bağış (SWIFT)', icon: Building2,
    desc: 'Yurt dışından yapılacak bağışlar için SWIFT bilgileri',
    fields: [
      { key: 'swift_bank', label: 'Banka Adı', icon: Building2, placeholder: 'Türkiye İş Bankası' },
      { key: 'swift_code', label: 'SWIFT Kodu', icon: Globe, placeholder: 'ISBKTRIS' },
      { key: 'swift_account_holder', label: 'Hesap Sahibi', icon: Building2, placeholder: '21. Yüzyıl Eğitim ve Kültür Vakfı' },
    ],
  },
  {
    label: 'Harita / Konum', icon: MapPin,
    desc: 'İletişim sayfasındaki harita iframe URL\'si',
    fields: [
      { key: 'site_map_iframe', label: 'Harita Embed URL', icon: MapPin, placeholder: 'https://www.openstreetmap.org/export/embed.html?...', long: true },
      { key: 'virtual_tour_url', label: 'Sanal Tur Linki (360°)', icon: Globe, placeholder: 'https://yekuv.org/yekuv360/', help: 'İletişim sayfasındaki 360° Sanal Tur kartının adresi. Boş bırakılırsa kart gizlenir. Yeni sekmede açılır.' },
    ],
  },
];

const ALL_KNOWN = GROUPS.flatMap(g => g.fields.map(f => f.key));

export default function AdminSettings() {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState({});
  const [extra, setExtra] = useState([]);
  const [newKey, setNewKey] = useState('');
  const [activeGroup, setActiveGroup] = useState(0);

  useEffect(() => {
    api.adminListSettings().then(list => {
      const m = {};
      list.forEach(s => { m[s.key] = s.value; });
      setValues(m);
      setExtra(list.filter(s => !ALL_KNOWN.includes(s.key)).map(s => ({ key: s.key, value: s.value })));
    });
  }, []);

  const save = async (key, value) => {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await api.adminUpsertSetting(key, value);
      toast.success('Kaydedildi');
    } catch {
      toast.error('Kaydedilemedi');
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  const saveAllInGroup = async (group) => {
    for (const f of group.fields) {
      // eslint-disable-next-line no-await-in-loop
      await api.adminUpsertSetting(f.key, values[f.key] || '');
    }
    toast.success(`${group.label} kaydedildi`);
  };

  const addCustom = () => {
    if (!newKey.trim()) return;
    setExtra(arr => [...arr, { key: newKey.trim(), value: '' }]);
    setNewKey('');
  };

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Ayarlar</div>
        <h1 className="font-display text-4xl mt-2 leading-none">Site Ayarları</h1>
        <p className="mt-2 text-ink-400 text-sm">İletişim, sosyal medya, footer ve diğer site bilgilerini düzenleyin.</p>
      </div>

      {/* Group tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {GROUPS.map((g, i) => {
          const Icon = g.icon;
          return (
            <button key={g.label} onClick={() => setActiveGroup(i)} data-testid={`tab-${i}`}
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-xs font-semibold border transition-colors ${activeGroup === i ? 'bg-ink-600 text-cream border-ink-600' : 'bg-white text-ink-500 border-ink-100 hover:border-ink-300'}`}>
              <Icon className="w-4 h-4" /> {g.label}
            </button>
          );
        })}
        <button onClick={() => setActiveGroup(99)}
          className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-xs font-semibold border transition-colors ${activeGroup === 99 ? 'bg-ink-600 text-cream border-ink-600' : 'bg-white text-ink-500 border-ink-100 hover:border-ink-300'}`}>
          <Plus className="w-4 h-4" /> Özel
        </button>
      </div>

      {activeGroup !== 99 && GROUPS[activeGroup] && (
        <div className="bg-white rounded-2xl border border-ink-100 p-6 lg:p-8">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">{GROUPS[activeGroup].label}</div>
              <h2 className="font-display text-2xl mt-1">{GROUPS[activeGroup].desc}</h2>
            </div>
            <button onClick={() => saveAllInGroup(GROUPS[activeGroup])} className="bg-ink-600 text-cream rounded-full px-5 h-10 text-sm font-semibold hover:bg-ink-500 inline-flex items-center gap-2">
              <Save className="w-4 h-4" /> Tümünü Kaydet
            </button>
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            {GROUPS[activeGroup].fields.map(f => (
              <Field
                key={f.key} f={f}
                value={values[f.key] || ''}
                onChange={v => setValues(s => ({ ...s, [f.key]: v }))}
                onSave={() => save(f.key, values[f.key] || '')}
                saving={saving[f.key]}
              />
            ))}
          </div>
        </div>
      )}

      {activeGroup === 99 && (
        <div className="bg-white rounded-2xl border border-ink-100 p-6 lg:p-8">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Özel Anahtarlar</div>
              <h2 className="font-display text-2xl mt-1">İleri seviye ayarlar</h2>
              <p className="text-sm text-ink-400 mt-1">Geliştirici ihtiyaçları için key-value ayarları.</p>
            </div>
            <div className="flex gap-2">
              <input value={newKey} onChange={e => setNewKey(e.target.value)} placeholder="ornek_anahtar" className="bg-cream-200 border border-ink-100 rounded-xl px-3 h-10 text-sm w-48" />
              <button onClick={addCustom} className="bg-ink-600 text-cream rounded-xl px-4 h-10 text-sm font-semibold hover:bg-ink-500 inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Ekle</button>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            {extra.map(e => (
              <Field
                key={e.key} f={{ key: e.key, label: e.key, placeholder: '' }}
                value={values[e.key] || ''}
                onChange={v => setValues(s => ({ ...s, [e.key]: v }))}
                onSave={() => save(e.key, values[e.key] || '')}
                saving={saving[e.key]}
              />
            ))}
            {extra.length === 0 && (
              <div className="col-span-2 py-8 text-center text-ink-400 text-sm">Henüz özel anahtar yok. Yukarıdan ekleyebilirsiniz.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ f, value, onChange, onSave, saving }) {
  const Icon = f.icon;
  return (
    <div className="bg-cream-200/60 rounded-xl border border-ink-100 p-4">
      <label className="text-[10px] tracking-[0.25em] uppercase text-ink-400 font-bold flex items-center gap-1.5">
        {Icon ? <Icon className="w-3 h-3" /> : null} {f.label}
      </label>
      <div className="mt-2 flex gap-2">
        {f.long ? (
          <textarea
            value={value} onChange={e => onChange(e.target.value)}
            placeholder={f.placeholder}
            rows={3}
            data-testid={`setting-${f.key}`}
            className="flex-1 bg-white border border-ink-100 focus:border-ink-500 rounded-xl px-3 py-2 text-sm outline-none resize-none"
          />
        ) : (
          <input
            value={value} onChange={e => onChange(e.target.value)}
            placeholder={f.placeholder}
            data-testid={`setting-${f.key}`}
            className="flex-1 bg-white border border-ink-100 focus:border-ink-500 rounded-xl px-3 h-10 text-sm outline-none"
          />
        )}
        <button onClick={onSave} disabled={saving} className="bg-gold text-ink-700 rounded-xl px-3 h-10 text-sm font-semibold hover:bg-gold-400 disabled:opacity-60 self-start">
          {saving ? '...' : <Save className="w-4 h-4" />}
        </button>
      </div>
      <div className="mt-1.5 text-[10px] text-ink-400 font-mono">{f.key}</div>
    </div>
  );
}
